/**
 * IOT-HAL-2: Hardware Abstraction Layer service — v2 rebuild.
 *
 * Single routing layer between all Kairos IoT consumers and the heterogeneous
 * hardware adapters. Upstream consumers never import vendor adapters directly;
 * they call HalService which resolves the correct adapter based on device.vendor.
 *
 * v2 additions:
 *   - Edge/cloud routing hook (D1 hybrid: premium=edge, standard=cloud)
 *   - Deterministic event ordering via sequenceNumber
 *   - Capability matrix surface (delegates to HalCapabilityMatrix when wired)
 *   - Schema-versioned device records (DeviceContract v2.0.0)
 *
 * Architecture constraints (FOUNDATION-PURITY-1):
 *   - Firestore reads/writes go through DataService ONLY (no direct Firebase imports)
 *   - All state-change events are emitted to EventBusService ONLY
 *   - No tenant-specific logic in this file
 *   - Capability reads go through getCapabilities(deviceId) — never reach into adapters directly
 */

import EventBusService from '../EventBusService.js';
import { HAL_EVENTS } from './contracts/HalEvents.ts';

// ── Adapter registry ──────────────────────────────────────────────────────────

/** @type {Map<string, Function>} vendorId → factory function */
const _adapterRegistry = new Map();

/** @type {Map<string, object>} deviceId → adapter instance */
const _instanceCache = new Map();

// ── Internal injections (set by app bootstrap) ────────────────────────────────

let _dataService = null;
let _capabilityMatrix = null;  // HalCapabilityMatrix — wired by W27-A Session 6
let _edgeTierSelector = null;  // EdgeTierSelector — wired by W27-A Session 5

// ── Sequence counter (deterministic event ordering) ───────────────────────────

let _eventSequence = 0;

function _nextSeq() {
  return ++_eventSequence;
}

// ── HalService ────────────────────────────────────────────────────────────────

const HalService = {

  /**
   * Register an adapter factory for a vendor.
   *
   * @param {string} vendor
   * @param {object|Function} adapterOrFactory - Adapter instance or factory
   */
  registerAdapter(vendor, adapterOrFactory) {
    if (adapterOrFactory === null || adapterOrFactory === undefined) {
      throw new Error(`HalService.registerAdapter: adapter/factory for "${vendor}" must not be null`);
    }
    if (typeof adapterOrFactory !== 'function' && typeof adapterOrFactory !== 'object') {
      throw new Error(`HalService.registerAdapter: factory for "${vendor}" must be a function`);
    }
    _adapterRegistry.set(vendor, adapterOrFactory);
  },

  /**
   * Inject DataService — called by app bootstrap after DataService is ready.
   * @param {object} dataService
   */
  injectDataService(dataService) {
    _dataService = dataService;
  },

  /**
   * Inject HalCapabilityMatrix — wired by Session 6 (IOT-HAL-CAPABILITY-MATRIX-1).
   * @param {object} capabilityMatrix
   */
  injectCapabilityMatrix(capabilityMatrix) {
    _capabilityMatrix = capabilityMatrix;
  },

  /**
   * Inject EdgeTierSelector — wired by Session 5 (IOT-EDGE-AI-RUNTIME-1).
   * @param {object} edgeTierSelector
   */
  injectEdgeTierSelector(edgeTierSelector) {
    _edgeTierSelector = edgeTierSelector;
  },

  /**
   * Resolve an adapter instance for a device.
   * Cache keyed by deviceId; invalidated on lifecycle transitions.
   *
   * @param {string} deviceId
   * @returns {Promise<object>}
   */
  async getAdapter(deviceId) {
    if (_instanceCache.has(deviceId)) {
      return _instanceCache.get(deviceId);
    }

    const device = await this._loadDevice(deviceId);
    const adapterOrFactory = _adapterRegistry.get(device.vendor);
    if (!adapterOrFactory) {
      throw new Error(
        `HalService.getAdapter: no adapter registered for vendor "${device.vendor}" (deviceId=${deviceId})`
      );
    }

    const adapter = typeof adapterOrFactory === 'function'
      ? adapterOrFactory(device)
      : adapterOrFactory;

    _instanceCache.set(deviceId, adapter);
    return adapter;
  },

  /**
   * Return the capability set for a device.
   * Routes through HalCapabilityMatrix when available (O(1) in Session 6),
   * otherwise falls back to direct adapter call.
   *
   * @param {string} deviceId
   * @returns {Promise<object>}
   */
  async getCapabilities(deviceId) {
    if (_capabilityMatrix) {
      return _capabilityMatrix.getCapabilities(deviceId);
    }
    const adapter = await this.getAdapter(deviceId);
    return typeof adapter.capabilities === 'function'
      ? adapter.capabilities()
      : adapter.getCapabilities?.();
  },

  /**
   * Resolve the compute tier for a device's tenant (premium=edge, standard=cloud).
   * Returns 'standard' if EdgeTierSelector has not yet been injected via injectEdgeTierSelector().
   *
   * @param {string} tenantId
   * @returns {Promise<'premium'|'standard'>}
   */
  async resolveEdgeTier(tenantId) {
    if (_edgeTierSelector) {
      return _edgeTierSelector.selectTier(tenantId);
    }
    return 'standard';
  },

  /**
   * Register a new device in Firestore and emit DEVICE_REGISTERED to EventBus.
   *
   * @param {object} deviceData
   * @param {object} actor
   * @returns {Promise<string>} deviceId
   */
  async registerDevice(deviceData, actor) {
    if (!deviceData.tenantId) throw new Error('HalService.registerDevice: tenantId required');
    if (!deviceData.vendor)   throw new Error('HalService.registerDevice: vendor required');
    if (!deviceData.model)    throw new Error('HalService.registerDevice: model required');

    if (!_adapterRegistry.has(deviceData.vendor)) {
      throw new Error(`HalService.registerDevice: no adapter registered for vendor "${deviceData.vendor}"`);
    }

    const deviceId = await this._persistDevice(deviceData);
    const edgeTier = await this.resolveEdgeTier(deviceData.tenantId);

    await EventBusService.publish({
      eventType:    HAL_EVENTS.DEVICE_REGISTERED,
      tenantId:     deviceData.tenantId,
      sourceModule: 'iot',
      sourceId:     deviceId,
      actor:        actor.uid || actor.agent || 'system',
      sequenceNumber: _nextSeq(),
      payload: {
        deviceId,
        vendor:         deviceData.vendor,
        model:          deviceData.model,
        lifecycleState: deviceData.lifecycleState || 'provisioned',
        edgeTier,
        schemaVersion:  '2.0.0',
      },
    });

    return deviceId;
  },

  /**
   * Transition a device's lifecycle state and emit DEVICE_LIFECYCLE_CHANGED.
   *
   * @param {string} deviceId
   * @param {string} newState
   * @param {object} actor
   */
  async setLifecycleState(deviceId, newState, actor) {
    const adapter = await this.getAdapter(deviceId);
    const previousState = typeof adapter.getLifecycleState === 'function'
      ? adapter.getLifecycleState()
      : 'unknown';

    if (typeof adapter.setLifecycleState === 'function') {
      await adapter.setLifecycleState(newState, actor);
    }

    // Invalidate cache — capabilities may change on activation
    _instanceCache.delete(deviceId);

    await EventBusService.publish({
      eventType:    HAL_EVENTS.DEVICE_LIFECYCLE_CHANGED,
      tenantId:     actor.tenantId,
      sourceModule: 'iot',
      sourceId:     deviceId,
      actor:        actor.uid || actor.agent || 'system',
      sequenceNumber: _nextSeq(),
      payload: {
        deviceId,
        previousState,
        newState,
        reason: actor.reason,
      },
    });
  },

  /**
   * Push a config delta to a device via its adapter and emit appropriate events.
   *
   * @param {string} deviceId
   * @param {object} delta
   * @param {object} actor
   * @returns {Promise<object>}
   */
  async pushConfig(deviceId, delta, actor) {
    const adapter = await this.getAdapter(deviceId);
    const capabilities = await this.getCapabilities(deviceId);

    if (capabilities && capabilities.configKeys) {
      for (const key of Object.keys(delta.changes || {})) {
        const cap = capabilities.configKeys[key];
        if (!cap) {
          throw new Error(`HalService.pushConfig: unknown config key "${key}" for device ${deviceId}`);
        }
        if (!cap.pushable) {
          throw new Error(`HalService.pushConfig: config key "${key}" is not pushable for device ${deviceId}`);
        }
        if (cap.requiresActivation) {
          throw new Error(`HalService.pushConfig: config key "${key}" requires activation-time setting`);
        }
      }
    }

    await EventBusService.publish({
      eventType:    HAL_EVENTS.CONFIG_PUSH_INITIATED,
      tenantId:     actor.tenantId,
      sourceModule: 'iot',
      sourceId:     deviceId,
      actor:        actor.uid || actor.agent || 'system',
      sequenceNumber: _nextSeq(),
      payload: { deviceId, delta },
    });

    const pushFn = adapter.pushConfig || adapter.send;
    const receipt = await pushFn.call(adapter, delta, actor);

    const successEvent = receipt?.status === 'applied'
      ? HAL_EVENTS.CONFIG_PUSH_APPLIED
      : receipt?.status === 'failed'
        ? HAL_EVENTS.CONFIG_PUSH_FAILED
        : HAL_EVENTS.CONFIG_PUSH_ACKNOWLEDGED;

    await EventBusService.publish({
      eventType:    successEvent,
      tenantId:     actor.tenantId,
      sourceModule: 'iot',
      sourceId:     deviceId,
      actor:        actor.uid || actor.agent || 'system',
      sequenceNumber: _nextSeq(),
      payload: { deviceId, receipt },
    });

    return receipt;
  },

  /**
   * Emit a normalized telemetry reading to EventBus.
   *
   * @param {object} reading
   */
  async emitTelemetry(reading) {
    await EventBusService.publish({
      eventType:    HAL_EVENTS.TELEMETRY_RECEIVED,
      tenantId:     reading.tenantId,
      sourceModule: 'iot',
      sourceId:     reading.deviceId,
      actor:        'system',
      sequenceNumber: _nextSeq(),
      payload:      reading,
    });
  },

  /**
   * Emit a canonical device event to EventBus.
   *
   * @param {object} event
   */
  async emitDeviceEvent(event) {
    await EventBusService.publish({
      eventType:    HAL_EVENTS.EVENT_RECEIVED,
      tenantId:     event.tenantId,
      sourceModule: 'iot',
      sourceId:     event.deviceId,
      actor:        'system',
      sequenceNumber: _nextSeq(),
      payload:      event,
    });
  },

  /** Clear the adapter instance cache for a device. */
  clearAdapterCache(deviceId) {
    _instanceCache.delete(deviceId);
  },

  /** Clear all cached adapter instances. Used in tests and on tenant switch. */
  clearAllAdapterCaches() {
    _instanceCache.clear();
  },

  // ── Internal helpers ──────────────────────────────────────────────────────

  async _loadDevice(deviceId) {
    if (_dataService) {
      return _dataService.getDevice(deviceId);
    }
    throw new Error(
      `HalService: DataService not injected. Call HalService.injectDataService(ds) at app bootstrap. ` +
      `(deviceId=${deviceId})`
    );
  },

  async _persistDevice(deviceData) {
    if (_dataService) {
      const doc = await _dataService.upsertDevice(deviceData);
      return doc.id;
    }
    return deviceData.id || `dev_${Date.now()}`;
  },

  // ── Test helpers ──────────────────────────────────────────────────────────

  _reset() {
    _adapterRegistry.clear();
    _instanceCache.clear();
    _dataService = null;
    _capabilityMatrix = null;
    _edgeTierSelector = null;
    _eventSequence = 0;
  },

  _registeredVendors() {
    return Array.from(_adapterRegistry.keys());
  },

  _getEventSequence() {
    return _eventSequence;
  },
};

export default HalService;
